import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { animate, stagger } from 'animejs';
import './HomePage.css';

// Placeholder for a phone-screen-shaped app screenshot slot
function PhoneSlot({ src, alt, label }) {
  return (
    <div className="phone-slot">
      {src ? (
        <img src={src} alt={alt} className="phone-slot-img" />
      ) : (
        <div className="phone-slot-placeholder">
          <span className="phone-slot-label">{label || 'App Screenshot'}</span>
        </div>
      )}
    </div>
  );
}

const HomePage = () => {
  const heroRef = useRef(null);
  const observerRef = useRef(null);

  // Hero entrance animation (runs after splash is done / on mount)
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    animate(hero.querySelectorAll('.hero-animate'), {
      opacity: [0, 1],
      translateY: [32, 0],
      duration: 700,
      ease: 'outQuad',
      delay: stagger(120),
    });
  }, []);

  // Scroll-triggered entrance animations
  useEffect(() => {
    const targets = document.querySelectorAll('.animate-on-scroll');

    // Set initial hidden state
    targets.forEach(el => {
      el.style.opacity = '0';
      el.style.transform = el.dataset.from || 'translateY(40px)';
    });

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            animate(entry.target, {
              opacity: [0, 1],
              translateY: [40, 0],
              translateX: entry.target.dataset.slideX
                ? [parseInt(entry.target.dataset.slideX), 0]
                : [0, 0],
              duration: 700,
              ease: 'outQuad',
            });
            observerRef.current.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );

    targets.forEach(el => observerRef.current.observe(el));

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  // Staggered card animations
  useEffect(() => {
    const groups = document.querySelectorAll('.stagger-group');

    const groupObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const children = entry.target.querySelectorAll('.stagger-item');
            children.forEach(child => { child.style.opacity = '0'; });
            animate(children, {
              opacity: [0, 1],
              translateY: [30, 0],
              duration: 600,
              ease: 'outQuad',
              delay: stagger(100),
            });
            groupObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    groups.forEach(g => groupObserver.observe(g));
    return () => groupObserver.disconnect();
  }, []);

  return (
    <div className="homepage">

      {/* ── Navbar ── */}
      <nav className="navbar">
        <div className="nav-content">
          <div className="nav-logo">
            <img src="/logo.png" alt="TasteCircle" className="logo-icon" />
            <span className="brand-name">TasteCircle</span>
          </div>
          <div className="nav-links">
            <a href="#features" className="nav-link">Features</a>
            <a href="#privacy" className="nav-link">Privacy</a>
            <a href="#download" className="nav-link">Download</a>
            <Link to="/terms" className="nav-link">Terms</Link>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="hero" ref={heroRef}>
        <div className="hero-inner">
          <img src="/logo.png" alt="" className="hero-logo hero-animate" aria-hidden="true" />
          <h1 className="hero-title hero-animate">
            Share Your&nbsp;Taste
          </h1>
          <p className="hero-subtitle hero-animate">
            A private space for your inner circle to discover and share
            daily food&nbsp;moments.
          </p>
          <a href="#download" className="cta-button hero-animate">
            Download on the App Store
          </a>
        </div>
      </section>

      {/* ── Features ── */}
      <section id="features" className="features">

        {/* Feature 1 */}
        <div className="feature-row">
          <div className="feature-text animate-on-scroll" data-slide-x="-60">
            <span className="feature-tag">Daily Moments</span>
            <h2 className="feature-heading">One photo.<br />Every day.</h2>
            <p className="feature-body">
              Share a single special food photo each day. No endless feed,
              no pressure — just one genuine moment with the people you love.
            </p>
          </div>
          <div className="feature-visual animate-on-scroll" data-slide-x="60">
            <PhoneSlot src="/TC2.png" alt="Daily Moments feature" />
          </div>
        </div>

        {/* Feature 2 — reversed */}
        <div className="feature-row feature-row--reversed">
          <div className="feature-text animate-on-scroll" data-slide-x="60">
            <span className="feature-tag">Private Circles</span>
            <h2 className="feature-heading">Only the people<br />who matter.</h2>
            <p className="feature-body">
              Create intimate groups with your closest friends and family.
              No strangers, no algorithms deciding who sees your food.
            </p>
          </div>
          <div className="feature-visual animate-on-scroll" data-slide-x="-60">
            <PhoneSlot src="/TC3.png" alt="Private Circles feature" />
          </div>
        </div>

        {/* Feature 3 */}
        <div className="feature-row">
          <div className="feature-text animate-on-scroll" data-slide-x="-60">
            <span className="feature-tag">Authentic Sharing</span>
            <h2 className="feature-heading">Real food.<br />Real people.</h2>
            <p className="feature-body">
              No filters, no follower counts. Just genuine food experiences
              shared honestly with the people in your circle.
            </p>
          </div>
          <div className="feature-visual animate-on-scroll" data-slide-x="60">
            <PhoneSlot src="/TC1.png" alt="Authentic Sharing feature" />
          </div>
        </div>

      </section>

      {/* ── How It Works ── */}
      <section className="how-it-works">
        <div className="container">
          <h2 className="section-title animate-on-scroll">How it works</h2>
          <div className="steps stagger-group">
            <div className="step stagger-item">
              <div className="step-number">01</div>
              <h3 className="step-title">Create your circle</h3>
              <p className="step-body">
                Invite the friends and family you actually share meals with.
              </p>
            </div>
            <div className="step stagger-item">
              <div className="step-number">02</div>
              <h3 className="step-title">Post your daily bite</h3>
              <p className="step-body">
                Snap what you're eating and share it — breakfast, lunch, a late-night snack.
              </p>
            </div>
            <div className="step stagger-item">
              <div className="step-number">03</div>
              <h3 className="step-title">Discover together</h3>
              <p className="step-body">
                See what your circle is eating, react, and get inspired to try new things.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Privacy ── */}
      <section id="privacy" className="privacy">
        <div className="container">
          <div className="privacy-grid">
            <div className="privacy-text animate-on-scroll" data-slide-x="-60">
              <h2 className="section-title">Your privacy,<br />protected.</h2>
              <p className="privacy-body">
                TasteCircle is built with privacy at its core. Your photos live only
                inside your chosen circle — no public profiles, no data mining,
                no ads.
              </p>
              <ul className="privacy-list stagger-group">
                <li className="privacy-item stagger-item">End-to-end encrypted photos</li>
                <li className="privacy-item stagger-item">No ads. No selling your data.</li>
                <li className="privacy-item stagger-item">You control who sees your content</li>
                <li className="privacy-item stagger-item">Delete your data anytime</li>
              </ul>
            </div>
            <div className="privacy-visual animate-on-scroll" data-slide-x="60">
              <PhoneSlot src="/TC2.png" alt="TasteCircle privacy" />
            </div>
          </div>
        </div>
      </section>

      {/* ── Download CTA ── */}
      <section id="download" className="download">
        <div className="container">
          <div className="download-inner animate-on-scroll">
            <img src="/logo.png" alt="TasteCircle" className="download-logo" />
            <h2 className="download-title">Start sharing today</h2>
            <p className="download-subtitle">
              Join food lovers already sharing their daily discoveries.
            </p>
            <a
              href="https://apps.apple.com/app/tastecircle3"
              target="_blank"
              rel="noopener noreferrer"
              className="app-store-btn"
            >
              <svg viewBox="0 0 135 40" className="app-store-svg" aria-label="Download on the App Store">
                <rect width="135" height="40" rx="8" fill="#000" />
                <text x="68" y="15" textAnchor="middle" fill="#fff" fontSize="9" fontFamily="-apple-system, BlinkMacSystemFont, sans-serif" letterSpacing="0.5">Download on the</text>
                <text x="68" y="30" textAnchor="middle" fill="#fff" fontSize="16" fontWeight="600" fontFamily="-apple-system, BlinkMacSystemFont, sans-serif">App Store</text>
              </svg>
            </a>
            <p className="coming-soon">Android version coming soon</p>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="footer">
        <div className="container footer-inner">
          <div className="footer-brand">
            <img src="/logo.png" alt="TasteCircle" className="footer-logo" />
            <span className="footer-name">TasteCircle</span>
          </div>

          {/* Social links */}
          <div className="footer-socials">
            {/* TikTok */}
            <a href="https://www.tiktok.com/@taste.circle.app" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="TikTok">
              <svg className="social-icon" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.32 6.32 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z"/>
              </svg>
            </a>
            {/* YouTube */}
            <a href="https://www.youtube.com/@Taste-Circle" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="YouTube">
              <svg className="social-icon" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </a>
            {/* Instagram */}
            <a href="https://www.instagram.com/tastecircleapp/" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="Instagram">
              <svg className="social-icon" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
          </div>

          <div className="footer-links">
            <Link to="/terms" className="footer-link">Terms of Service</Link>
            <a href="mailto:support@tastecircle.app" className="footer-link">Support</a>
          </div>
          <p className="footer-copy">&copy; {new Date().getFullYear()} TasteCircle. All rights reserved.</p>
        </div>
      </footer>

    </div>
  );
};

export default HomePage;
